<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import gsap from 'gsap'
import { useGlobalStore } from '@stores/global'
import Button from '@components/global/forms/Button.vue'
import Underline from '@components/global/accents/Underline.vue'
import HonourRibbon from '@components/global/accents/HonourRibbon.vue'

const global = useGlobalStore()

const shuffledIndices = (count) => Array.from({ length: count }, (_, i) => i).sort(() => Math.random() - 0.5)

const menuLinksEl = ref(null)

const resetLinkChars = () => {
	if (!menuLinksEl.value) return
	const wrapperChars = [...menuLinksEl.value.querySelectorAll('.menu-link-char')]
	const primaryChars = [...menuLinksEl.value.querySelectorAll('.menu-link-primary')]
	const hoverChars = [...menuLinksEl.value.querySelectorAll('.menu-link-hover')]

	gsap.killTweensOf([...wrapperChars, ...primaryChars, ...hoverChars])
	gsap.set(wrapperChars, { width: '', marginRight: '' })
	gsap.set(primaryChars, { opacity: 1 })
	gsap.set(hoverChars, { opacity: 0, y: 4 })
}

onMounted(() => window.addEventListener('resize', resetLinkChars))
onUnmounted(() => window.removeEventListener('resize', resetLinkChars))

const enterLink = (e) => {
	const wrapperChars = [...e.currentTarget.querySelectorAll('.menu-link-char')]
	const primaryChars = [...e.currentTarget.querySelectorAll('.menu-link-primary')]
	const hoverChars = [...e.currentTarget.querySelectorAll('.menu-link-hover')]

	gsap.killTweensOf([...wrapperChars, ...primaryChars, ...hoverChars])

	shuffledIndices(primaryChars.length).forEach((charIndex, staggerIndex) => {
		const delay = Math.min(staggerIndex * 0.015, 0.07)
		gsap.to(wrapperChars[charIndex], { width: hoverChars[charIndex].offsetWidth, marginRight: '0.1em', duration: 0.1, delay, ease: 'power2.out' })
		gsap.to(primaryChars[charIndex], { opacity: 0, duration: 0.1, delay, ease: 'power2.out' })
		gsap.to(hoverChars[charIndex], { opacity: 1, y: 0, duration: 0.1, delay, ease: 'power2.out' })
	})
}

const leaveLink = (e) => {
	const wrapperChars = [...e.currentTarget.querySelectorAll('.menu-link-char')]
	const primaryChars = [...e.currentTarget.querySelectorAll('.menu-link-primary')]
	const hoverChars = [...e.currentTarget.querySelectorAll('.menu-link-hover')]

	gsap.killTweensOf([...wrapperChars, ...primaryChars, ...hoverChars])

	shuffledIndices(primaryChars.length).forEach((charIndex, staggerIndex) => {
		const delay = Math.min(staggerIndex * 0.015, 0.07)
		gsap.to(wrapperChars[charIndex], { width: primaryChars[charIndex].offsetWidth, marginRight: 0, duration: 0.1, delay, ease: 'power2.in' })
		gsap.to(primaryChars[charIndex], { opacity: 1, duration: 0.1, delay, ease: 'power2.in' })
		gsap.to(hoverChars[charIndex], { opacity: 0, y: 4, duration: 0.1, delay, ease: 'power2.in' })
	})
}

const socials = ref([
	{
		title: 'Behance',
		icon: 'fab fa-behance',
		href: 'https://behance.com/papercrowns',
	},
	{
		title: 'X (Twitter)',
		icon: 'fab fa-x-twitter',
		href: 'https://www.x.com/papercrowns',
	},
	{
		title: 'Instagram',
		icon: 'fab fa-instagram',
		href: 'https://www.instagram.com/papercrowns',
	},
	{
		title: 'LinkedIn',
		icon: 'fab fa-linkedin',
		href: 'https://www.linkedin.com/company/papercrowns',
	},
])
</script>

<template>
	<div class="flex h-full flex-col justify-between gap-2 p-5">
		<router-link :to="{ name: 'playground' }" class="group mt-2.5 flex w-fit cursor-pointer items-center gap-5 pl-3 text-2xl" @click="global.closeModal()">
			<img src="https://cdn.papercrowns.com/website/images/elements/etches/candle.svg" alt="Candle" class="h-10 md:h-12" />
			<div class="hidden -translate-x-3 opacity-0 transition-all duration-500 ease-in-out group-hover:translate-x-0 md:block md:group-hover:opacity-100">
				<div class="pt-2 font-semibold leading-none">For the wanderer</div>
				<Underline fill="#000000" class="w-48" />
			</div>
		</router-link>

		<div class="overflow-y-auto pl-5">
			<ul ref="menuLinksEl" class="flex flex-col gap-1 md:gap-3">
				<li v-for="link in global.navigation" @mouseenter="enterLink" @mouseleave="leaveLink">
					<router-link :to="link.to" class="inline-flex" @click="global.closeModal()">
						<span v-for="(char, i) in link.title" :key="i" class="menu-link-char relative inline-block whitespace-pre">
							<span class="menu-link-primary font-alternate-gothic text-4xl uppercase md:text-5xl xl:text-6xl">{{ char }}</span>
							<span class="menu-link-hover absolute left-0 top-0 font-cabazon text-4xl uppercase -tracking-wide opacity-0 md:text-5xl xl:text-6xl">{{ char }}</span>
						</span>
					</router-link>
				</li>
				<Button class="large menu-text dark mb-10 mt-2" :to="{ name: 'contact' }" @click="global.closeModal()">Contact Us</Button>
			</ul>
		</div>
		<div class="flex flex-col gap-4 md:gap-8">
			<div class="mb-5 flex items-center gap-5">
				<a class="text-md font-semibold leading-none md:text-lg lg:text-xl" href="https://craftedbycrowns.com" target="_blank">
					Crafted
					<br />
					by Crowns
				</a>
				<ul class="flex grow items-center justify-end gap-2">
					<li v-for="social in socials">
						<Button variant="social" :icon="social.icon" :href="social.href" @click="global.closeModal()" />
					</li>
				</ul>
			</div>
			<div>
				<ul class="flex gap-5">
					<li v-for="link in global.policyPages">
						<router-link class="text-sm text-crowns-castle-stone transition-colors duration-200 hover:text-crowns-off-black" :to="{ name: 'policy', params: { slug: link.slug } }" @click="global.closeModal()">
							{{ link.title }}
						</router-link>
					</li>
				</ul>
			</div>
		</div>
	</div>

	<button class="absolute bottom-2 right-0 -mr-4 flex h-12 w-14 transition-all duration-200 ease-in-out hover:-mr-2 md:bottom-5 md:w-20" @click="global.openModal('ColorHonor')">
		<HonourRibbon class="h-12" :accent="global.colorOfHonor.hex" />
	</button>
</template>
